"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useSession } from "next-auth/react";

import logo from "@/assets/images/logo-vivaldis.svg";
import Badge from "@/components/atoms/Badge";
import CardLinkNavigationIndicator from "@/components/atoms/CardLinkNavigationIndicator";
import Tag from "@/components/atoms/Tag";
import SaveJobButton from "@/components/molecules/SaveJobButton";
import { cn } from "@/helpers/className";
import { getPreviousApplication } from "@/helpers/cookies";
import { withRetry } from "@/lib/helpers/dynamicRetryImport";
import { generateJobUrl } from "@/helpers/url";
import { useTranslation } from "@/i18n/client";
import { AllowedLanguages } from "@/types/generic";

export interface JobCardJob {
  city: string | null;
  description: string | null;
  id: string;
  internal: boolean;
  regime: string | null;
  sector_group: string | null;
  specialty: string | null;
  title: string;
  type: string | null;
  urgent: boolean | null;
}

interface JobCardProps {
  className?: string;
  job: JobCardJob;
  locale: AllowedLanguages;
  small?: boolean;
}

const VisitedNoSSR = dynamic(
  withRetry(() => import("@/components/molecules/Visited")),
  { ssr: false },
);

// WARNING: don't make this ASYNC! It will mess up job overview (load more).
export const JobCard = ({
  job,
  locale,
  small = false,
  className,
}: JobCardProps) => {
  const { data: isLoggedIn } = useSession();
  const { t } = useTranslation(locale);
  const applied = getPreviousApplication(job.id);
  const VisitedBadge = (
    <Badge color={"green"} icon="eye">
      {t("components.job_card.viewed")}
    </Badge>
  );

  return (
    <div
      key={job.id}
      className={cn(
        "relative flex flex-col rounded-lg border bg-white shadow transition-all hover:border-yellow-600 hover:shadow-lg",
        job.internal ? "group border-4 border-yellow" : "",
        className,
      )}
    >
      {job.internal && (
        <div className="flex items-center justify-between bg-yellow px-6 py-1.5 transition-colors group-hover:bg-yellow-600 lg:px-10 lg:py-4">
          <h5 className="!mb-0">{t("components.job_card.internal")}</h5>
          <Image
            src={logo}
            width={80}
            height={42}
            alt="Vivaldis Interim"
            className="w-[80px]"
          />
        </div>
      )}
      <div className={cn("p-6 pb-5", !small && "lg:p-10 lg:pb-9")}>
        <div className="flex items-start justify-between gap-2">
          <div className="max-w-full">
            <div className="mb-4 flex flex-wrap items-start justify-start gap-2 empty:hidden lg:mb-6">
              {job.urgent && (
                <Badge color={"orange"} icon="fire">
                  {t("components.job_card.urgent")}
                </Badge>
              )}
              {applied ? (
                <Badge color={"brown"} icon="check">
                  {t("components.job_card.applied_on")}{" "}
                  {new Date(applied.date).toLocaleDateString(locale)}
                </Badge>
              ) : (
                <VisitedNoSSR
                  VisitedComponent={VisitedBadge}
                  pageId={job.id}
                  pageType={"job"}
                  writeCookie={false}
                />
              )}
            </div>

            <h4
              className={cn("h5 !mb-1", !small && "lg:h4 break-words lg:!mb-2")}
            >
              {job.title}
            </h4>

            <div
              className={cn(
                "text-sm text-black-300",
                !small && "font-medium lg:text-lg",
              )}
            >
              {job.city}
            </div>
          </div>

          {isLoggedIn && <SaveJobButton jobId={job.id} />}
        </div>

        <hr className="my-3 lg:my-4" />

        {job.description && (
          <div
            className={cn(
              "prose line-clamp-3 max-w-3xl max-w-none text-sm text-black-300",
              !small && "lg:text-base",
            )}
            dangerouslySetInnerHTML={{
              __html: job.description.replace(/<\/?[^>]+(>|$)/g, ""),
            }}
          ></div>
        )}
      </div>

      <div
        className={cn(
          "mt-auto flex flex-wrap items-center gap-2 border-t px-6 py-4",
          !small && "lg:px-10 lg:py-6",
        )}
      >
        <Tag color={"gray"} icon={"tag"} small={small}>
          {t(`common.filter.options.${job.specialty}`)}
        </Tag>

        <Tag color={"gray"} icon={"tag"} small={small}>
          {t(`common.filter.options.${job.sector_group}`)}
        </Tag>
        <Tag color={"gray"} icon={"clock"} small={small}>
          {t(`common.filter.options.${job.regime}`)}
        </Tag>
        <Tag color={"gray"} icon={"calendar"} small={small}>
          {t(`common.filter.options.${job.type}`)}
        </Tag>
      </div>

      <Link
        href={generateJobUrl(locale, job.title, job.id)}
        aria-label={t("components.job_card.aria.view_job_details_link_label", {
          title: job.title,
        })}
        className="stretched-link"
        prefetch={false}
      >
        <CardLinkNavigationIndicator className="rounded-lg" />
      </Link>
    </div>
  );
};
